import { useEffect, useState } from "react";
import { Loader2, Clock, Check, X, RefreshCw, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATUS = {
  waiting: { label: "In attesa", cls: "bg-amber-50 text-amber-700" },
  seated: { label: "Seduto", cls: "bg-[#5aa469]/12 text-[#4a8a56]" },
  removed: { label: "Rimosso", cls: "bg-gray-100 text-gray-500" },
};

export default function Waitlist() {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/waitlist");
      setEntries(data);
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); const i = setInterval(load, 20000); return () => clearInterval(i); }, []);

  const setStatus = async (id, status) => {
    const { data } = await api.put(`/waitlist/${id}`, { status });
    setEntries((w) => w.map((x) => (x.id === id ? data : x)));
  };
  const remove = async (id) => {
    await api.delete(`/waitlist/${id}`);
    setEntries((w) => w.filter((x) => x.id !== id));
    toast.success("Voce rimossa");
  };

  if (error) return (
    <div className="max-w-lg rounded-2xl border border-amber-200 bg-amber-50/60 p-6" data-testid="waitlist-locked">
      <h1 className="font-display font-bold text-3xl mb-2">Lista d'attesa</h1>
      <p className="text-gray-600">{error}</p>
    </div>
  );
  if (entries === null) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#5aa469]" /></div>;

  const waiting = entries.filter((e) => e.status === "waiting");

  return (
    <div className="space-y-6" data-testid="waitlist-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display font-bold text-4xl">Lista d'attesa</h1>
          <p className="text-gray-500 mt-1"><strong>{waiting.length}</strong> {waiting.length === 1 ? "gruppo in attesa" : "gruppi in attesa"}.</p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-full border-gray-300 bg-white hover:bg-gray-50" data-testid="refresh-waitlist-btn"><RefreshCw className="h-4 w-4 mr-1.5" />Aggiorna</Button>
      </div>

      {entries.length === 0 && (
        <div className="text-center py-16 rounded-2xl border border-dashed border-gray-300 bg-white">
          <Clock className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Nessuno in lista d'attesa.</p>
        </div>
      )}

      <div className="space-y-3">
        {entries.map((e) => {
          const st = STATUS[e.status] || STATUS.waiting;
          return (
            <div key={e.id} className="rounded-2xl mo-card p-4 flex items-center gap-4" data-testid={`waitlist-${e.id}`}>
              <div className="h-11 w-11 rounded-full bg-[#5aa469]/12 flex items-center justify-center shrink-0"><Users className="h-5 w-5 text-[#4a8a56]" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">{e.name} <span className="text-gray-400 text-sm">· {e.party_size} pers.</span></p>
                <p className="text-xs text-gray-400">{new Date(e.created_at).toLocaleString("it-IT")}{e.phone && <> · {e.phone}</>}</p>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${st.cls}`}>{st.label}</span>
              <div className="flex gap-1">
                {e.status === "waiting" && <Button size="sm" onClick={() => setStatus(e.id, "seated")} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white text-xs" data-testid={`waitlist-seat-${e.id}`}><Check className="h-3.5 w-3.5 mr-1" />Fai sedere</Button>}
                <Button size="sm" variant="ghost" onClick={() => remove(e.id)} className="text-red-500 hover:bg-red-50 text-xs" data-testid={`waitlist-delete-${e.id}`}><X className="h-4 w-4" /></Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
